import { useEffect, useRef, useState } from 'react';
import type { PageType } from '@gumtree/shared/src/types/client-data';

export const getActiveStickyNavElement = () =>
    document.querySelector<HTMLElement>('[data-q="mhh-nav-bar"]') ||
    document.querySelector<HTMLElement>('[data-q="nav-bar"]');

export const useActiveStickyNavElement = (enabled: boolean) => {
    const [activeStickyNavElement, setActiveStickyNavElement] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setActiveStickyNavElement(enabled ? getActiveStickyNavElement() : null);
    }, [enabled]);

    return activeStickyNavElement;
};

export const isHomepage = (pageType: PageType) => pageType === 'Homepage';

export const calculateStickyThreshold = ({
    navHeight,
    scrollY,
    sentinelTop,
}: {
    navHeight: number;
    scrollY: number;
    sentinelTop: number;
}) => Math.max(0, sentinelTop + scrollY - navHeight);

export const useHeaderStickyState = ({
    enabled,
    preserveSticky,
}: {
    enabled: boolean;
    preserveSticky: boolean;
}) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [isSticky, setIsSticky] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setIsSticky(false);
            return;
        }

        const sentinel = sentinelRef.current;
        if (!sentinel) {
            return;
        }

        let stickyThreshold = 0;

        const updateStickyThreshold = () => {
            const navBarHeight =
                getActiveStickyNavElement()?.getBoundingClientRect().height || 0;
            stickyThreshold = calculateStickyThreshold({
                navHeight: navBarHeight,
                scrollY: window.scrollY,
                sentinelTop: sentinel.getBoundingClientRect().top,
            });
        };

        const updateStickyState = () => {
            setIsSticky((currentIsSticky) =>
                preserveSticky && currentIsSticky ? true : window.scrollY > stickyThreshold
            );
        };

        const updateStickyLayout = () => {
            updateStickyThreshold();
            updateStickyState();
        };

        updateStickyLayout();
        window.addEventListener('scroll', updateStickyState, { passive: true });
        window.addEventListener('resize', updateStickyLayout);

        return () => {
            window.removeEventListener('scroll', updateStickyState);
            window.removeEventListener('resize', updateStickyLayout);
        };
    }, [enabled, preserveSticky]);

    return { isSticky, sentinelRef };
};
